import { KNOWLEDGE_ITEMS } from '@/services/knowledge/knowledgeCatalog.js'
import type { KnowledgeItem } from '@/types/knowledge.js'
import type { UserDatabase } from '@/types/user-data.js'

export interface ContentReferenceIndex {
  knowledgeIds: ReadonlySet<string>
  questionToKnowledge: ReadonlyMap<string, string>
}

export interface ContentReferenceIssue {
  path: string
  message: string
}

export function createContentReferenceIndex(
  items: readonly KnowledgeItem[] = KNOWLEDGE_ITEMS,
): ContentReferenceIndex {
  const knowledgeIds = new Set(items.map((item) => item.id))
  const questionToKnowledge = new Map(
    items.flatMap((item) => item.questions.map((question) => [question.id, item.id] as const)),
  )
  return { knowledgeIds, questionToKnowledge }
}

export function findOrphanRecordIds(
  database: Pick<
    UserDatabase,
    'progress' | 'wrongQuestions' | 'questionAttempts' | 'practiceSessions'
  >,
  index: ContentReferenceIndex = createContentReferenceIndex(),
): {
  progressIds: string[]
  wrongQuestionIds: string[]
  questionAttemptIds: string[]
  practiceSessionIds: string[]
} {
  return {
    progressIds: Object.keys(database.progress).filter((id) => !index.knowledgeIds.has(id)),
    wrongQuestionIds: Object.keys(database.wrongQuestions).filter(
      (id) => !index.questionToKnowledge.has(id),
    ),
    questionAttemptIds: Object.values(database.questionAttempts)
      .filter(
        (attempt) =>
          !index.questionToKnowledge.has(attempt.questionId) ||
          !index.knowledgeIds.has(attempt.knowledgeId),
      )
      .map((attempt) => attempt.id),
    practiceSessionIds: Object.values(database.practiceSessions)
      .filter((session) =>
        session.questionIds.some((questionId) => !index.questionToKnowledge.has(questionId)),
      )
      .map((session) => session.id),
  }
}

export function validateContentReferences(
  database: UserDatabase,
  index: ContentReferenceIndex = createContentReferenceIndex(),
  options: { allowOrphans?: boolean } = {},
): ContentReferenceIssue[] {
  const allowOrphans = options.allowOrphans ?? false
  const issues: ContentReferenceIssue[] = []

  for (const [id, progress] of Object.entries(database.progress)) {
    if (progress.knowledgeId !== id) {
      issues.push({
        path: `data.progress.${id}.knowledgeId`,
        message: `记录 ID 与 knowledgeId 不一致（应为 ${id}）。`,
      })
    }
    if (!allowOrphans && !index.knowledgeIds.has(id)) {
      issues.push({ path: `data.progress.${id}`, message: `找不到关联知识点 ${id}。` })
    }
  }

  for (const [id, record] of Object.entries(database.wrongQuestions)) {
    if (record.questionId !== id) {
      issues.push({
        path: `data.wrongQuestions.${id}.questionId`,
        message: `记录 ID 与 questionId 不一致（应为 ${id}）。`,
      })
    }
    const expectedKnowledgeId = index.questionToKnowledge.get(id)
    if (expectedKnowledgeId && expectedKnowledgeId !== record.knowledgeId) {
      issues.push({
        path: `data.wrongQuestions.${id}.knowledgeId`,
        message: `题目 ${id} 关联的知识点应为 ${expectedKnowledgeId}。`,
      })
    }
    if (!allowOrphans && !expectedKnowledgeId) {
      issues.push({ path: `data.wrongQuestions.${id}`, message: `找不到关联题目 ${id}。` })
    }
    if (!allowOrphans && !index.knowledgeIds.has(record.knowledgeId)) {
      issues.push({
        path: `data.wrongQuestions.${id}.knowledgeId`,
        message: `找不到关联知识点 ${record.knowledgeId}。`,
      })
    }
  }

  for (const [id, attempt] of Object.entries(database.questionAttempts)) {
    if (attempt.id !== id) {
      issues.push({
        path: `data.questionAttempts.${id}.id`,
        message: `记录 ID 与 Attempt.id 不一致（应为 ${id}）。`,
      })
    }
    const expectedKnowledgeId = index.questionToKnowledge.get(attempt.questionId)
    if (expectedKnowledgeId && expectedKnowledgeId !== attempt.knowledgeId) {
      issues.push({
        path: `data.questionAttempts.${id}.knowledgeId`,
        message: `题目 ${attempt.questionId} 关联的知识点应为 ${expectedKnowledgeId}。`,
      })
    }
    if (!allowOrphans && !expectedKnowledgeId) {
      issues.push({
        path: `data.questionAttempts.${id}.questionId`,
        message: `找不到关联题目 ${attempt.questionId}。`,
      })
    }
    if (!allowOrphans && !index.knowledgeIds.has(attempt.knowledgeId)) {
      issues.push({
        path: `data.questionAttempts.${id}.knowledgeId`,
        message: `找不到关联知识点 ${attempt.knowledgeId}。`,
      })
    }

    const session = database.practiceSessions[attempt.sessionId]
    if (!session) {
      issues.push({
        path: `data.questionAttempts.${id}.sessionId`,
        message: `找不到关联练习 Session ${attempt.sessionId}。`,
      })
    } else {
      if (!session.attemptIds.includes(id)) {
        issues.push({
          path: `data.questionAttempts.${id}.sessionId`,
          message: `Session ${attempt.sessionId} 未引用该 Attempt。`,
        })
      }
      if (attempt.mode !== session.mode || attempt.category !== session.category) {
        issues.push({
          path: `data.questionAttempts.${id}.mode`,
          message: '答题记录的练习模式或分类与 Session 不一致。',
        })
      }
    }
  }

  for (const [id, session] of Object.entries(database.practiceSessions)) {
    if (session.id !== id) {
      issues.push({
        path: `data.practiceSessions.${id}.id`,
        message: `记录 ID 与 Session.id 不一致（应为 ${id}）。`,
      })
    }
    if (!allowOrphans) {
      for (const [questionIndex, questionId] of session.questionIds.entries()) {
        if (!index.questionToKnowledge.has(questionId)) {
          issues.push({
            path: `data.practiceSessions.${id}.questionIds.${questionIndex}`,
            message: `找不到关联题目 ${questionId}。`,
          })
        }
      }
    }
    for (const [attemptIndex, attemptId] of session.attemptIds.entries()) {
      const attempt = database.questionAttempts[attemptId]
      if (!attempt) {
        issues.push({
          path: `data.practiceSessions.${id}.attemptIds.${attemptIndex}`,
          message: `找不到答题记录 ${attemptId}。`,
        })
        continue
      }
      if (attempt.sessionId !== id) {
        issues.push({
          path: `data.practiceSessions.${id}.attemptIds.${attemptIndex}`,
          message: `答题记录 ${attemptId} 属于其他 Session。`,
        })
      }
      if (attempt.questionId !== session.questionIds[attemptIndex]) {
        issues.push({
          path: `data.practiceSessions.${id}.attemptIds.${attemptIndex}`,
          message: `答题记录 ${attemptId} 与 Session 题目顺序不一致。`,
        })
      }
    }
  }

  return issues
}

export function reconcileContentVersion(
  database: UserDatabase,
  contentVersion: string,
  index: ContentReferenceIndex = createContentReferenceIndex(),
  migratedAt = new Date(),
): UserDatabase {
  if (database.contentVersion === contentVersion) return database
  const orphans = findOrphanRecordIds(database, index)
  const timestamp = migratedAt.toISOString()
  return {
    ...database,
    contentVersion,
    updatedAt: timestamp,
    contentMigration: {
      fromContentVersion: database.contentVersion,
      toContentVersion: contentVersion,
      migratedAt: timestamp,
      preservedProgressCount: Object.keys(database.progress).length,
      preservedWrongQuestionCount: Object.keys(database.wrongQuestions).length,
      preservedQuestionAttemptCount: Object.keys(database.questionAttempts).length,
      preservedPracticeSessionCount: Object.keys(database.practiceSessions).length,
      orphanProgressIds: orphans.progressIds,
      orphanWrongQuestionIds: orphans.wrongQuestionIds,
      orphanQuestionAttemptIds: orphans.questionAttemptIds,
      orphanPracticeSessionIds: orphans.practiceSessionIds,
    },
  }
}
